import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";

import Footer from "../components/Footer";

/* ==========================================================================
   CONFIGURATION — c'est ici (et seulement ici) qu'on modifie la page
   ========================================================================== */

/**
 * Accès au parcours "génération immédiate".
 *
 * Le mot de passe n'existe plus dans ce fichier : il est vérifié par
 * api/creator-login.js, qui le compare en temps constant à l'empreinte
 * stockée dans CREATOR_ADMIN_PASSWORD_HASH et renvoie un cookie de session
 * signé, HttpOnly. Ce cookie est la seule chose qui autorise la création d'un
 * code — la page ne décide plus rien, elle ne fait qu'afficher la réponse.
 *
 * Ne jamais réintroduire de secret ici : tout ce qui vit dans src/ finit
 * lisible dans le bundle public.
 */
const LOGIN_ENDPOINT = "/api/creator-login";

/** Proxy vers l'Azure Function AddCreatorCode. Voir api/creator-codes.js. */
const CREATOR_CODES_ENDPOINT = "/api/creator-codes";

/**
 * Destination des CANDIDATURES (parcours B). Laisser vide = MODE DÉMO : la
 * payload part dans la console du navigateur et la page simule un succès.
 *
 * Pointé sur notre propre API : la candidature crée désormais le code pour de
 * bon, via exactement le même chemin que le parcours A (voir
 * api/_lib/creator-code.js). L'écran de résultat parle toujours d'une demande
 * "en attente" alors que le code est actif — c'est assumé, le front n'a pas
 * été retouché.
 *
 * RÈGLES DÉLÉGUÉES AU BACKEND (volontairement non vérifiées ici) :
 *  - au moins un réseau social renseigné ;
 *  - unicité du code (répondre 409 si le code est déjà pris).
 */
const WEBHOOK_URL = "/api/creator-apply";

/** Logo affiché en tête de page. */
const LOGO_URL = "/assets/Legends_Of_The_Past_logo_white.png";

/** Nombre de Gemmes offertes au joueur qui saisit un code créateur. */
const GEM_REWARD = 200;

/** Bornes de longueur du pseudo, une fois normalisé. */
const MIN_PSEUDO_LENGTH = 3;
const MAX_PSEUDO_LENGTH = 12;

/** Longueur minimale de la motivation (parcours candidature). */
const MIN_MOTIVATION_LENGTH = 30;

/** Anti-force-brute : X tentatives de mot de passe par fenêtre de Y ms. */
const MAX_PASSWORD_ATTEMPTS = 5;
const PASSWORD_WINDOW_MS = 60 * 1000;

/** Pseudos interdits car réservés à l'équipe. */
const RESERVED_PSEUDOS = [
  "ADMIN",
  "LOP",
  "OFFICIAL",
  "SUPPORT",
  "MODERATOR",
  "STAFF",
];

/** Filtre d'insultes basique (FR + EN). À compléter au besoin. */
const BLOCKED_WORDS = [
  "CONNARD",
  "CONNASSE",
  "SALOPE",
  "PUTE",
  "PUTAIN",
  "ENCULE",
  "BATARD",
  "MERDE",
  "NIQUE",
  "PEDE",
  "FUCK",
  "SHIT",
  "BITCH",
  "ASSHOLE",
  "CUNT",
  "DICK",
  "SLUT",
  "WHORE",
  "NIGGER",
  "RAPE",
  "NAZI",
];

/** Langue affichée au chargement. Le visiteur peut basculer à tout moment. */
const DEFAULT_LOCALE = "en";

/* ==========================================================================
   TEXTES — tout ce qui est visible à l'écran, une entrée par langue.
   Pour ajouter une langue : dupliquer un bloc, traduire, l'ajouter à
   LOCALES ci-dessous. Aucun texte visible ne doit vivre ailleurs.
   ========================================================================== */

const TRANSLATIONS = {
  en: {
    meta: {
      title: "Creator Program — Legends Of The Past",
      description:
        "Get your Legends Of The Past creator code and give your community " +
        GEM_REWARD +
        " Gems.",
    },

    brand: {
      logoAlt: "Legends Of The Past",
      subtitle: "Creator Program",
    },

    languageLabel: "Language",

    intro: {
      whyCreatorTitle: "Why become a creator?",
      whyCreatorPoints: ["Creators who bring in players get paid."],
      whyPlayerTitle: "Why would a player use your code?",
      whyPlayerPoints: [
        "Every player who enters your code gets " +
          GEM_REWARD +
          " Gems (redeemable for 1 Skin or 50 Gacha Keys).",
      ],
    },

    gate: {
      label: "Enter your password to generate your code",
      placeholder: "PASSWORD",
      submit: "Submit",
      loading: "Checking...",
      secondary: "No password? Apply",
      wrongPassword: "Wrong password.",
      emptyPassword: "Enter your password to continue.",
      tooManyAttempts: "Too many attempts, try again in a moment.",
      serverError: "Server unreachable. Try again in a moment.",
      sessionExpired: "Your session expired. Enter your password again.",
    },

    pseudo: {
      label: "Your nickname",
      placeholder: "nickname",
      hint: "Your code will be generated from your nickname.",
      tooShort:
        MIN_PSEUDO_LENGTH + " characters minimum (letters and numbers).",
      reserved: "This nickname is reserved, pick another one.",
      blocked: "This nickname isn't allowed, pick another one.",
    },

    socials: {
      legend: "Your socials",
      tiktok: "TikTok",
      tiktokPlaceholder: "@nickname",
      youtube: "YouTube",
      youtubePlaceholder: "youtube.com/@nickname",
      instagram: "Instagram",
      instagramPlaceholder: "@nickname",
      twitch: "Twitch",
      twitchPlaceholder: "twitch.tv/nickname",
    },

    generate: {
      submit: "Generate my code",
      loading: "Generating...",
    },

    apply: {
      title: "Application",
      motivationLabel: "Why do you want to become a LOP creator?",
      motivationPlaceholder:
        "Your community, your content, where you post...",
      counter: (n) => n + " / " + MIN_MOTIVATION_LENGTH + " characters minimum",
      counterDone: (n) => n + " characters",
      emailLabel: "Your email, to receive your code if we pick you",
      emailPlaceholder: "you@example.com",
      emailInvalid: "This email doesn't look valid.",
      submit: "Send my application",
      loading: "Sending...",
      back: "Back",
    },

    result: {
      pendingBadge: "Pending",
      copy: "Copy",
      copied: "Copied!",
      share:
        "Share it with your community. Players enter it in the in-game shop.",
      pending:
        "Your code is reserved. If we pick your profile, we'll email it to you.",
    },

    errors: {
      network: "Sending failed. Your answers have been kept.",
      retry: "Try again",
      conflict: "This code is already taken",
      conflictHint: "Pick a variant:",
      missingSocial:
        "Add at least one social account so we can review your profile.",
      invalidCode:
        "This nickname produces an invalid code. Use only letters and numbers.",
      server: "Code creation is unavailable right now. Ping the LOP team.",
    },

  },

  fr: {
    meta: {
      title: "Programme Créateurs — Legends Of The Past",
      description:
        "Obtiens ton code créateur Legends Of The Past et fais gagner " +
        GEM_REWARD +
        " Gemmes à ta communauté.",
    },

    brand: {
      logoAlt: "Legends Of The Past",
      subtitle: "Programme Créateurs",
    },

    languageLabel: "Langue",

    intro: {
      whyCreatorTitle: "Pourquoi devenir créateur ?",
      whyCreatorPoints: [
        "Les créateurs qui font venir des joueurs sont rémunérés.",
      ],
      whyPlayerTitle: "Pourquoi un joueur utiliserait ton code ?",
      whyPlayerPoints: [
        "Chaque joueur qui entre ton code obtient " +
          GEM_REWARD +
          " Gemmes (échangeables contre 1 Skin ou 50 Clés de Gacha).",
      ],
    },

    gate: {
      label: "Entre ton mot de passe pour générer ton code",
      placeholder: "MOT DE PASSE",
      submit: "Valider",
      loading: "Vérification...",
      secondary: "Pas de mot de passe ? Candidate",
      wrongPassword: "Mot de passe incorrect.",
      emptyPassword: "Entre ton mot de passe pour continuer.",
      tooManyAttempts: "Trop de tentatives, réessaie dans un instant.",
      serverError: "Serveur injoignable. Réessaie dans un instant.",
      sessionExpired: "Ta session a expiré. Entre à nouveau ton mot de passe.",
    },

    pseudo: {
      label: "Ton pseudo",
      placeholder: "pseudo",
      hint: "Ton code sera généré à partir de ton pseudo.",
      tooShort:
        MIN_PSEUDO_LENGTH + " caractères minimum (lettres et chiffres).",
      reserved: "Ce pseudo est réservé, choisis-en un autre.",
      blocked: "Ce pseudo n'est pas autorisé, choisis-en un autre.",
    },

    socials: {
      legend: "Tes réseaux",
      tiktok: "TikTok",
      tiktokPlaceholder: "@pseudo",
      youtube: "YouTube",
      youtubePlaceholder: "youtube.com/@pseudo",
      instagram: "Instagram",
      instagramPlaceholder: "@pseudo",
      twitch: "Twitch",
      twitchPlaceholder: "twitch.tv/pseudo",
    },

    generate: {
      submit: "Générer mon code",
      loading: "Génération...",
    },

    apply: {
      title: "Candidature",
      motivationLabel: "Pourquoi veux-tu devenir créateur LOP ?",
      motivationPlaceholder:
        "Ta communauté, ton contenu, la plateforme où tu publies...",
      counter: (n) => n + " / " + MIN_MOTIVATION_LENGTH + " caractères minimum",
      counterDone: (n) => n + " caractères",
      emailLabel: "Ton email, pour recevoir ton code si on te retient",
      emailPlaceholder: "toi@exemple.com",
      emailInvalid: "Cet email n'a pas l'air valide.",
      submit: "Envoyer ma candidature",
      loading: "Envoi...",
      missingMotivation:
        "une motivation de " + MIN_MOTIVATION_LENGTH + " caractères",
      back: "Retour",
    },

    result: {
      pendingBadge: "En attente",
      copy: "Copier",
      copied: "Copié !",
      share:
        "Partage-le à ta communauté. Les joueurs le saisissent dans la boutique du jeu.",
      pending:
        "Ton code est réservé. Si on retient ton profil, on te l'envoie par email.",
    },

    errors: {
      network: "L'envoi a échoué. Tes réponses sont conservées.",
      retry: "Réessayer",
      conflict: "Ce code est déjà pris",
      conflictHint: "Choisis une variante :",
      missingSocial:
        "Ajoute au moins un réseau pour qu'on puisse étudier ton profil.",
      invalidCode:
        "Ce pseudo produit un code invalide. Utilise uniquement des lettres et des chiffres.",
      server:
        "La création de code est indisponible pour le moment. Préviens l'équipe LOP.",
    },

  },

  "pt-BR": {
    meta: {
      title: "Programa de Criadores — Legends Of The Past",
      description:
        "Pegue seu código de criador de Legends Of The Past e dê " +
        GEM_REWARD +
        " Gemas para a sua comunidade.",
    },

    brand: {
      logoAlt: "Legends Of The Past",
      subtitle: "Programa de Criadores",
    },

    languageLabel: "Idioma",

    intro: {
      whyCreatorTitle: "Por que ser criador?",
      whyCreatorPoints: [
        "Os criadores que trazem jogadores são remunerados.",
      ],
      whyPlayerTitle: "Por que um jogador usaria seu código?",
      whyPlayerPoints: [
        "Cada jogador que digita seu código ganha " +
          GEM_REWARD +
          " Gemas (trocáveis por 1 Skin ou 50 Chaves de Gacha).",
      ],
    },

    gate: {
      label: "Digite sua senha para gerar seu código",
      placeholder: "SENHA",
      submit: "Validar",
      loading: "Verificando...",
      secondary: "Sem senha? Candidate-se",
      wrongPassword: "Senha incorreta.",
      emptyPassword: "Digite sua senha para continuar.",
      tooManyAttempts: "Muitas tentativas, tente de novo em instantes.",
      serverError: "Servidor indisponível. Tente de novo em instantes.",
      sessionExpired: "Sua sessão expirou. Digite sua senha novamente.",
    },

    pseudo: {
      label: "Seu apelido",
      placeholder: "apelido",
      hint: "Seu código será gerado a partir do seu apelido.",
      tooShort:
        MIN_PSEUDO_LENGTH + " caracteres no mínimo (letras e números).",
      reserved: "Este apelido é reservado, escolha outro.",
      blocked: "Este apelido não é permitido, escolha outro.",
    },

    socials: {
      legend: "Suas redes",
      tiktok: "TikTok",
      tiktokPlaceholder: "@apelido",
      youtube: "YouTube",
      youtubePlaceholder: "youtube.com/@apelido",
      instagram: "Instagram",
      instagramPlaceholder: "@apelido",
      twitch: "Twitch",
      twitchPlaceholder: "twitch.tv/apelido",
    },

    generate: {
      submit: "Gerar meu código",
      loading: "Gerando...",
    },

    apply: {
      title: "Candidatura",
      motivationLabel: "Por que você quer ser criador LOP?",
      motivationPlaceholder:
        "Sua comunidade, seu conteúdo, onde você publica...",
      counter: (n) =>
        n + " / " + MIN_MOTIVATION_LENGTH + " caracteres no mínimo",
      counterDone: (n) => n + " caracteres",
      emailLabel: "Seu e-mail, para receber seu código se a gente te escolher",
      emailPlaceholder: "voce@exemplo.com",
      emailInvalid: "Este e-mail não parece válido.",
      submit: "Enviar minha candidatura",
      loading: "Enviando...",
      missingMotivation:
        "uma motivação de " + MIN_MOTIVATION_LENGTH + " caracteres",
      back: "Voltar",
    },

    result: {
      pendingBadge: "Aguardando",
      copy: "Copiar",
      copied: "Copiado!",
      share:
        "Compartilhe com a sua comunidade. Os jogadores digitam na loja do jogo.",
      pending:
        "Seu código está reservado. Se escolhermos seu perfil, enviamos por e-mail.",
    },

    errors: {
      network: "Falha no envio. Suas respostas foram mantidas.",
      retry: "Tentar de novo",
      conflict: "Este código já está em uso",
      conflictHint: "Escolha uma variante:",
      missingSocial:
        "Adicione pelo menos uma rede social para podermos analisar seu perfil.",
      invalidCode:
        "Este apelido gera um código inválido. Use apenas letras e números.",
      server:
        "A criação de código está indisponível agora. Avise a equipe LOP.",
    },

  },
};
/** Ordre d'affichage du sélecteur de langue. */
const LOCALES = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "pt-BR", label: "PT-BR" },
];

/**
 * Style de titre repris du bloc "KPIS" de la home : blanc, gras, italique,
 * majuscules, avec l'ombre portée bleue définie dans global.css.
 */
// leading généreux : l'ombre portée déborde de 4px sous chaque ligne et
// viendrait mordre la ligne suivante avec l'interligne par défaut.
const TITLE_CLASS =
  "uppercase italic font-bold text-white text-shadow--blue leading-[1.7]";

/**
 * Style du bouton principal, repris du bouton CONTACT du footer : doré,
 * italique majuscules, avec le halo pulsé (keyframes glowPulse plus bas).
 */
/**
 * Taille du corps de texte, alignée sur le paragraphe "STORY" de la home
 * (font-montserrat text-base lg:text-lg xl:text-xl leading-relaxed).
 * C'est le plancher : aucun texte de lecture ne doit descendre en dessous.
 */
const BODY_CLASS =
  "font-montserrat text-sm sm:text-base lg:text-lg leading-relaxed";

const CTA_CLASS =
  "animate-pulse transform hover:scale-105 hover:shadow-2xl bg-[#FFD700] " +
  "hover:bg-[#e6c200] text-black font-bold italic uppercase rounded-xl " +
  "transition-all duration-300";

const LocaleContext = createContext(TRANSLATIONS[DEFAULT_LOCALE]);
const useT = () => useContext(LocaleContext);

/* ==========================================================================
   RÉSEAU — point d'entrée unique de tous les appels sortants
   ========================================================================== */

/**
 * Vérifie le mot de passe côté serveur. En cas de succès, le navigateur
 * reçoit un cookie de session HttpOnly qu'aucun script de la page ne peut
 * lire — d'où l'absence de valeur retournée en dehors du statut.
 *
 * @returns {Promise<{ok: true} | {ok: false, reason: "wrongPassword"|"tooManyAttempts"|"serverError"}>}
 * @throws en cas d'erreur réseau (le formulaire propose "Réessayer").
 */
async function login(password) {
  const response = await fetch(LOGIN_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ password }),
  });

  if (response.ok) return { ok: true };
  if (response.status === 401) return { ok: false, reason: "wrongPassword" };
  if (response.status === 429) return { ok: false, reason: "tooManyAttempts" };
  return { ok: false, reason: "serverError" };
}

/**
 * Crée réellement le code créateur, via le proxy serveur qui appelle
 * l'Azure Function. Le cookie de session part avec la requête grâce à
 * credentials: "same-origin".
 *
 * Seul `code` sert à créer quoi que ce soit. Le reste de la payload (pseudo,
 * réseaux, langue) n'est là que pour la feuille de suivi tenue côté serveur :
 * si ces champs manquent, le code est créé quand même.
 *
 * @param {object} payload  pseudo, code, socials, email, motivation, locale
 * @returns {Promise<{ok: boolean, conflict?: boolean, invalidCode?: boolean, expired?: boolean, serverError?: boolean}>}
 * @throws en cas d'erreur réseau (le formulaire propose "Réessayer").
 */
async function createCreatorCode(payload) {
  const response = await fetch(CREATOR_CODES_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });

  // Session absente ou expirée : il faut repasser par le mot de passe.
  if (response.status === 403) {
    return { ok: false, expired: true };
  }

  const data = await response.json().catch(() => ({}));

  if (response.ok && data.ok !== false) {
    return { ok: true };
  }

  if (data.error === "code_already_exists") {
    return { ok: false, conflict: true };
  }
  if (data.error === "invalid_code_format") {
    return { ok: false, invalidCode: true };
  }
  if (data.error === "missing_owner" || data.error === "server_misconfigured") {
    return { ok: false, serverError: true };
  }

  // Tout le reste (500, réponse amont illisible) : erreur réseau générique,
  // avec un bouton "Réessayer".
  throw new Error("HTTP " + response.status);
}

/**
 * Envoie une CANDIDATURE (parcours B). Ne crée aucun code : la demande est
 * étudiée à la main, le code est ensuite généré via le parcours A.
 *
 * @param {object} payload  pseudo, code, socials, email, motivation, locale
 * @returns {Promise<{ok: boolean, conflict?: boolean, missingSocial?: boolean}>}
 * @throws en cas d'erreur réseau ou HTTP (le formulaire propose "Réessayer").
 */
async function submitApplication(payload) {
  const body = {
    type: "application",
    ...payload,
    createdAt: new Date().toISOString(),
  };

  // Mode démo : pas de webhook configuré, on log et on simule un succès.
  if (!WEBHOOK_URL) {
    // eslint-disable-next-line no-console
    console.log("[LOP creator] mode démo, payload non envoyée :", body);
    await new Promise((resolve) => setTimeout(resolve, 500));
    return { ok: true };
  }

  const response = await fetch(WEBHOOK_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  // 409 = code déjà attribué à un autre créateur.
  if (response.status === 409) {
    return { ok: false, conflict: true };
  }
  // 422 = le backend refuse la soumission (ex : aucun réseau renseigné).
  if (response.status === 422) {
    return { ok: false, missingSocial: true };
  }
  if (!response.ok) {
    throw new Error("HTTP " + response.status);
  }

  const data = await response.json().catch(() => ({}));
  if (data && (data.conflict === true || data.error === "conflict")) {
    return { ok: false, conflict: true };
  }
  if (data && data.error === "missing_social") {
    return { ok: false, missingSocial: true };
  }
  return { ok: true };
}

/* ==========================================================================
   OUTILS
   ========================================================================== */

/** Majuscules, sans accents, sans caractère hors A-Z0-9, tronqué. */
function normalizePseudo(raw) {
  return (raw || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, MAX_PSEUDO_LENGTH);
}

/**
 * Le code créateur est le pseudo lui-même, sans suffixe.
 *
 * Conservé comme fonction plutôt qu'inliné : c'est le point unique où la
 * forme du code est décidée, côté saisie comme côté affichage.
 */
function buildCode(pseudo) {
  return pseudo || "";
}

/** @returns {null|"tooShort"|"reserved"|"blocked"} */
function getPseudoError(pseudo) {
  if (pseudo.length < MIN_PSEUDO_LENGTH) return "tooShort";
  if (RESERVED_PSEUDOS.indexOf(pseudo) !== -1) return "reserved";
  if (BLOCKED_WORDS.some((word) => pseudo.indexOf(word) !== -1)) return "blocked";
  return null;
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((email || "").trim());
}

/** Au moins un réseau renseigné. Le backend revérifie de son côté. */
function hasAtLeastOneSocial(socials) {
  return Object.keys(socials).some((key) => socials[key].trim() !== "");
}

/**
 * Trois replis quand le code est déjà pris : le pseudo suivi d'un nombre
 * tiré entre 0 et 99 (LENA7, LENA42, LENA83).
 *
 * Les trois nombres sont distincts, sans quoi la même suggestion pourrait
 * apparaître deux fois. Le tirage n'est pas confronté aux codes existants :
 * un repli peut lui aussi être pris, auquel cas le backend répond 409 et
 * trois nouvelles suggestions sont proposées.
 *
 * La troncature garde la variante sous MAX_PSEUDO_LENGTH : elle est
 * réinjectée comme pseudo quand l'utilisateur la choisit.
 */
function buildCodeVariants(pseudo) {
  const numbers = new Set();
  while (numbers.size < 3) {
    numbers.add(Math.floor(Math.random() * 100));
  }
  return Array.from(numbers, (number) => {
    const suffix = String(number);
    return pseudo.slice(0, MAX_PSEUDO_LENGTH - suffix.length) + suffix;
  });
}

/* ==========================================================================
   ICÔNES — SVG inline, aucune image à télécharger
   ========================================================================== */

const ICONS = {
  tiktok: (
    <path d="M13 3h2.2a4.8 4.8 0 0 0 4.3 4.3V9.6a7 7 0 0 1-4.3-1.5v6.3a5.4 5.4 0 1 1-5.4-5.4c.3 0 .5 0 .8.05v2.3a3.1 3.1 0 1 0 2.4 3V3z" />
  ),
  youtube: (
    <path d="M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8zM10 15V9l5.2 3-5.2 3z" />
  ),
  instagram: (
    <path d="M7.5 3h9A4.5 4.5 0 0 1 21 7.5v9a4.5 4.5 0 0 1-4.5 4.5h-9A4.5 4.5 0 0 1 3 16.5v-9A4.5 4.5 0 0 1 7.5 3zm0 2A2.5 2.5 0 0 0 5 7.5v9A2.5 2.5 0 0 0 7.5 19h9a2.5 2.5 0 0 0 2.5-2.5v-9A2.5 2.5 0 0 0 16.5 5h-9zM12 7.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM17.2 6a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2z" />
  ),
  twitch: (
    <path d="M4.3 3h15.4v11.1l-4.3 4.3h-3.4L9.4 21H7v-2.6H4.3V3zm2 2v11.4h3v2.2l2.2-2.2h3.6l3-3V5H6.3zm5.1 2.6h1.8v4.5h-1.8V7.6zm4.5 0h1.8v4.5h-1.8V7.6z" />
  ),
};

const SOCIAL_KEYS = ["tiktok", "youtube", "instagram", "twitch"];

const EMPTY_SOCIALS = { tiktok: "", youtube: "", instagram: "", twitch: "" };

/* ==========================================================================
   SOUS-COMPOSANTS
   ========================================================================== */

const FieldLabel = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    className="mb-2 block text-sm uppercase tracking-[0.15em] text-white/70"
  >
    {children}
  </label>
);

const inputClass =
  "w-full rounded-xl border border-white/15 bg-[color:var(--lop-surface)] px-4 py-3 " +
  "text-base text-white placeholder-white/30 transition-colors duration-200 " +
  "hover:border-white/30 focus:border-[#FFD700]";

/** Champ texte anti-bot : invisible à l'oeil, ignoré au clavier, rempli par les bots. */
const Honeypot = ({ id, value, onChange }) => (
  <div className="lop-honeypot" aria-hidden="true">
    <label htmlFor={id}>Do not fill this field</label>
    <input
      id={id}
      name="company"
      type="text"
      tabIndex={-1}
      autoComplete="off"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
);

const PseudoField = ({ id, value, onChange, error }) => {
  const t = useT();
  return (
    <div>
      <FieldLabel htmlFor={id}>{t.pseudo.label}</FieldLabel>
      <input
        id={id}
        name="pseudo"
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck="false"
        maxLength={MAX_PSEUDO_LENGTH + 10}
        placeholder={t.pseudo.placeholder}
        value={value}
        onChange={(event) => onChange(normalizePseudo(event.target.value))}
        aria-describedby={id + "-hint"}
        className={inputClass + " font-bold uppercase tracking-[0.2em]"}
      />
      <p id={id + "-hint"} className={"mt-2 text-white/60 " + BODY_CLASS}>
        {t.pseudo.hint}
      </p>
      {error && (
        <p className="mt-1 text-sm text-[color:var(--lop-error)]">
          {t.pseudo[error]}
        </p>
      )}
    </div>
  );
};

/**
 * Bloc réseaux. Aucun champ n'est obligatoire côté page : c'est le backend
 * qui refuse une soumission sans aucun réseau (voir WEBHOOK_URL).
 */
const SocialsBlock = ({ idPrefix, socials, onChange }) => {
  const t = useT();
  return (
    <fieldset className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <legend className="px-2 text-sm font-bold uppercase italic tracking-[0.15em] text-[#FFD700]">
        {t.socials.legend}
      </legend>
      <div className="flex flex-col gap-3 pt-2">
        {SOCIAL_KEYS.map((key) => {
          const id = idPrefix + "-" + key;
          return (
            <div key={key}>
              <FieldLabel htmlFor={id}>{t.socials[key]}</FieldLabel>
              <div className="relative">
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                  className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 fill-white/45"
                >
                  {ICONS[key]}
                </svg>
                <input
                  id={id}
                  name={key}
                  type="text"
                  autoComplete="off"
                  spellCheck="false"
                  placeholder={t.socials[key + "Placeholder"]}
                  value={socials[key]}
                  onChange={(event) => onChange(key, event.target.value)}
                  className={inputClass + " pl-11"}
                />
              </div>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
};

/**
 * Titre de section : lettrage repris du "Queen Of Egypt" de la home (blanc,
 * gras, italique), en majuscules et sans habillage.
 */
const SectionTitle = ({ children }) => (
  <h2 className="text-sm font-bold uppercase italic leading-tight text-white sm:text-lg">
    {children}
  </h2>
);

/** Explication du programme, affichée en haut de page quel que soit le parcours. */
const Explainer = () => {
  const t = useT();
  const block = (title, points) => (
    <div>
      <SectionTitle>{title}</SectionTitle>
      <ul className="mt-4 flex flex-col gap-3">
        {points.map((point) => (
          <li key={point} className={"flex gap-2 text-white/85 " + BODY_CLASS}>
            <span aria-hidden="true" className="text-[color:var(--lop-accent)]">
              •
            </span>
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <section className="mb-7 flex flex-col gap-7 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-6">
      {block(t.intro.whyCreatorTitle, t.intro.whyCreatorPoints)}
      {block(t.intro.whyPlayerTitle, t.intro.whyPlayerPoints)}
    </section>
  );
};

const PrimaryButton = ({ disabled, loading, loadingLabel, children, onClick, type }) => {
  const inactive = disabled || loading;
  return (
    <button
      type={type || "button"}
      disabled={inactive}
      onClick={onClick}
      // Le halo n'est animé que quand le bouton est réellement actionnable.
      style={inactive ? undefined : { animation: "glowPulse 2s infinite" }}
      className={
        "w-full px-6 py-4 text-base md:text-lg tracking-wide " +
        (inactive
          ? "cursor-not-allowed rounded-xl bg-white/10 font-bold uppercase italic text-white/40"
          : CTA_CLASS)
      }
    >
      {loading ? loadingLabel : children}
    </button>
  );
};

const NetworkError = ({ onRetry, retrying }) => {
  const t = useT();
  return (
    <div className="rounded-xl border border-[color:var(--lop-error)]/50 bg-[color:var(--lop-error)]/10 p-4">
      <p className={"text-white/85 " + BODY_CLASS}>{t.errors.network}</p>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="mt-3 rounded-lg border border-white/30 px-4 py-2 text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:border-white disabled:opacity-50"
      >
        {t.errors.retry}
      </button>
    </div>
  );
};

/** Message simple, utilisé quand le backend refuse (ex : aucun réseau). */
const RejectionNotice = ({ message }) => (
  <div
    role="alert"
    className="rounded-xl border border-[color:var(--lop-error)]/50 bg-[color:var(--lop-error)]/10 p-4"
  >
    <p className={"text-white/85 " + BODY_CLASS}>{message}</p>
  </div>
);

const ConflictNotice = ({ variants, onPick }) => {
  const t = useT();
  return (
    <div className="rounded-xl border border-[color:var(--lop-error)]/50 bg-[color:var(--lop-error)]/10 p-4">
      <p className="text-sm font-bold text-white">{t.errors.conflict}</p>
      <p className="mt-1 text-sm text-white/70">{t.errors.conflictHint}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {variants.map((variant) => (
          <button
            key={variant}
            type="button"
            onClick={() => onPick(variant)}
            className="rounded-lg border border-[color:var(--lop-accent)]/60 px-3 py-2 text-sm font-bold tracking-[0.1em] text-[color:var(--lop-accent)] transition-colors duration-200 hover:bg-[color:var(--lop-accent)]/15"
          >
            {buildCode(variant)}
          </button>
        ))}
      </div>
    </div>
  );
};

/** Carte de code, en version définitive (parcours A) ou en aperçu (parcours B). */
const CodeCard = ({ code, pending }) => {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        const helper = document.createElement("textarea");
        helper.value = code;
        helper.setAttribute("readonly", "");
        helper.style.position = "absolute";
        helper.style.left = "-9999px";
        document.body.appendChild(helper);
        helper.select();
        document.execCommand("copy");
        document.body.removeChild(helper);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <section className="lop-reveal flex flex-col gap-4">
      <div className="lop-card relative overflow-hidden rounded-2xl border border-[color:var(--lop-accent)]/50 px-4 py-10 text-center">
        {pending && (
          <span className="absolute right-3 top-3 rounded-full border border-white/30 bg-black/40 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.1em] text-white/80">
            {t.result.pendingBadge}
          </span>
        )}
        <p className="lop-code break-all text-[1.75rem] font-bold leading-tight tracking-[0.12em] text-[color:var(--lop-accent)] sm:text-[2.5rem]">
          {code}
        </p>
      </div>

      {pending ? (
        <p className={"text-center text-white/70 " + BODY_CLASS}>
          {t.result.pending}
        </p>
      ) : (
        <>
          <button
            type="button"
            onClick={copy}
            style={{ animation: "glowPulse 2s infinite" }}
            className={CTA_CLASS + " w-full px-6 py-4 text-base md:text-lg tracking-wide"}
          >
            {copied ? t.result.copied : t.result.copy}
          </button>
          <p className={"text-center text-white/70 " + BODY_CLASS}>
            {t.result.share}
          </p>
        </>
      )}
    </section>
  );
};

/** Sélecteur de langue : bascule instantanée, aucune donnée saisie n'est perdue. */
const LanguageSwitch = ({ locale, onChange, label }) => (
  <div
    role="group"
    aria-label={label}
    className="flex overflow-hidden rounded-lg border border-white/25"
  >
    {LOCALES.map((entry) => {
      const active = entry.code === locale;
      return (
        <button
          key={entry.code}
          type="button"
          onClick={() => onChange(entry.code)}
          aria-pressed={active}
          className={
            "px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors duration-200 " +
            (active
              ? "bg-[#FFD700] text-black"
              : "text-white/70 hover:text-white")
          }
        >
          {entry.label}
        </button>
      );
    })}
  </div>
);

/* ==========================================================================
   PAGE
   ========================================================================== */

const Creator = () => {
  const [locale, setLocale] = useState(DEFAULT_LOCALE);
  const t = TRANSLATIONS[locale];

  // "gate" = mot de passe, "generate" = parcours A, "apply" = parcours B
  const [step, setStep] = useState("gate");

  // --- Écran d'accueil
  const [password, setPassword] = useState("");
  const [gateError, setGateError] = useState(null);
  const [gateLoading, setGateLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const attemptsRef = useRef([]);

  // --- Champs partagés
  const [pseudo, setPseudo] = useState("");
  const [socials, setSocials] = useState(EMPTY_SOCIALS);
  const [motivation, setMotivation] = useState("");
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");

  // --- Soumission
  const [submitting, setSubmitting] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  // Clé de t.errors quand le serveur refuse pour une raison affichable, sinon null.
  const [rejection, setRejection] = useState(null);
  const [conflictVariants, setConflictVariants] = useState(null);
  const [result, setResult] = useState(null); // { code, pending }

  // Une fois la suite du formulaire de candidature révélée, elle ne se cache plus.
  const [applyRevealed, setApplyRevealed] = useState(false);

  const resultRef = useRef(null);

  const pseudoError = pseudo.length > 0 ? getPseudoError(pseudo) : null;
  const pseudoValid = pseudo.length > 0 && pseudoError === null;
  const socialValid = hasAtLeastOneSocial(socials);
  const emailValid = isValidEmail(email);
  const motivationValid = motivation.trim().length >= MIN_MOTIVATION_LENGTH;

  useEffect(() => {
    if (motivationValid) setApplyRevealed(true);
  }, [motivationValid]);

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  const updateSocial = (key, value) => {
    setSocials((previous) => ({ ...previous, [key]: value }));
  };

  const resetForms = () => {
    setPseudo("");
    setSocials(EMPTY_SOCIALS);
    setMotivation("");
    setEmail("");
    setHoneypot("");
    setResult(null);
    setNetworkError(false);
    setRejection(null);
    setConflictVariants(null);
    setApplyRevealed(false);
  };

  const goToApply = () => {
    setStep("apply");
    setGateError(null);
    setPassword("");
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 250);
  };

  /**
   * Soumet le mot de passe au serveur. Le compteur local n'est qu'un confort
   * d'interface pour éviter le martèlement : la vraie limite est appliquée
   * par api/creator-login.js, hors de portée du navigateur.
   *
   * Le champ met la saisie en majuscules, donc c'est bien la forme MAJUSCULE
   * du mot de passe qui doit être hachée dans CREATOR_ADMIN_PASSWORD_HASH.
   */
  const checkPassword = async () => {
    if (gateLoading) return;

    const now = Date.now();
    attemptsRef.current = attemptsRef.current.filter(
      (stamp) => now - stamp < PASSWORD_WINDOW_MS
    );
    if (attemptsRef.current.length >= MAX_PASSWORD_ATTEMPTS) {
      setGateError("tooManyAttempts");
      triggerShake();
      return;
    }

    const submitted = password.trim().toUpperCase();
    if (submitted === "") {
      setGateError("emptyPassword");
      triggerShake();
      return;
    }

    attemptsRef.current.push(now);
    setGateLoading(true);
    setGateError(null);

    try {
      const response = await login(submitted);
      if (response.ok) {
        // Le cookie de session est posé : on vide la saisie pour ne pas la
        // laisser traîner dans le DOM.
        setPassword("");
        attemptsRef.current = [];
        setStep("generate");
        return;
      }
      // Mot de passe faux : on garde la saisie, on signale l'erreur.
      setGateError(response.reason);
      triggerShake();
    } catch (error) {
      setGateError("serverError");
      triggerShake();
    } finally {
      setGateLoading(false);
    }
  };

  /** Payload commune aux deux parcours. */
  const buildPayload = (type, codePseudo) => ({
    pseudo: codePseudo,
    code: buildCode(codePseudo),
    socials: {
      tiktok: socials.tiktok.trim(),
      youtube: socials.youtube.trim(),
      instagram: socials.instagram.trim(),
      twitch: socials.twitch.trim(),
    },
    email: type === "application" ? email.trim() : "",
    motivation: type === "application" ? motivation.trim() : "",
    locale,
  });

  /**
   * Envoi commun : gère loading, conflit, refus backend, erreur réseau.
   *
   * "generate" crée le code pour de bon dans PlayFab, via le proxy serveur.
   * "application" n'enregistre qu'une demande : aucun code n'est créé tant
   * qu'un humain ne l'a pas générée depuis le parcours A.
   */
  const send = async (type, codePseudo) => {
    if (submitting) return; // anti double-submit
    if (honeypot !== "") return; // bot détecté : on ne fait rien, sans le dire

    setSubmitting(true);
    setNetworkError(false);
    setRejection(null);
    setConflictVariants(null);

    try {
      const response =
        type === "generate"
          ? await createCreatorCode(buildPayload(type, codePseudo))
          : await submitApplication(buildPayload(type, codePseudo));

      if (response.expired) {
        // Session expirée pendant la saisie : retour au mot de passe. Les
        // champs restent en state, il n'y a rien à retaper.
        setStep("gate");
        setGateError("sessionExpired");
        return;
      }
      if (response.conflict) {
        setConflictVariants(buildCodeVariants(codePseudo));
      } else if (response.invalidCode) {
        setRejection("invalidCode");
      } else if (response.serverError) {
        setRejection("server");
      } else if (response.missingSocial) {
        setRejection("missingSocial");
      } else {
        setResult({ code: buildCode(codePseudo), pending: type === "application" });
      }
    } catch (error) {
      // Les saisies restent en state : rien n'est perdu, on propose de réessayer.
      setNetworkError(true);
    } finally {
      setSubmitting(false);
    }
  };


  // Le bouton reste désactivé tant que tout n'est pas rempli : pas de message
  // listant ce qui manque, l'état du bouton suffit.
  const generateReady = pseudoValid && socialValid;
  const applyReady = motivationValid && pseudoValid && socialValid && emailValid;

  return (
    <LocaleContext.Provider value={t}>
      <section className="creator-page min-h-screen overflow-hidden bg-[color:var(--lop-bg)] text-white">
        <Helmet>
          <html lang={locale} />
          <title>{t.meta.title}</title>
          <meta name="description" content={t.meta.description} />
        </Helmet>

        {/* Palette et micro-interactions. Modifier les couleurs ici. */}
        <style>{`
          .creator-page {
            /* mint.css impose body { text-align: center } à toute l'app.
               On repart d'un alignement à gauche : les blocs qui doivent être
               centrés le demandent explicitement via text-center. */
            text-align: left;
            --lop-bg: #0B0A10;
            --lop-surface: #16141F;
            --lop-accent: #FC9B0E;
            --lop-text: #FFFFFF;
            --lop-error: #FF6B6B;
            --lop-success: #4ADE80;
          }
          /* global.css neutralise tous les outlines (*:focus { outline: none }).
             On rétablit un anneau de focus visible, condition d'accessibilité au
             clavier. :focus couvre tous les navigateurs, :focus-visible évite de
             l'afficher au clic là où il est supporté. */
          .creator-page :focus {
            outline: 3px solid #FFD700;
            outline-offset: 2px;
          }
          .creator-page :focus:not(:focus-visible) {
            outline: none;
          }
          .creator-page :focus-visible {
            outline: 3px solid #FFD700;
            outline-offset: 2px;
          }
          /* Champ actif : même halo pulsé que le bouton principal. */
          .creator-page input:focus,
          .creator-page textarea:focus {
            animation: glowPulse 2s infinite;
          }
          /* L'ombre de global.css (2px 4px opaque) est calibrée pour les très
             gros titres de la home ; à nos tailles elle écrase le texte.
             Override local uniquement : la home n'est pas touchée. */
          .creator-page .text-shadow--blue {
            text-shadow: 1.5px 3px rgba(17, 143, 242, 0.85);
          }
          .lop-honeypot {
            position: absolute;
            left: -9999px;
            width: 1px;
            height: 1px;
            overflow: hidden;
          }
          .lop-card {
            background:
              radial-gradient(120% 100% at 50% 0%, rgba(252, 155, 14, 0.22), transparent 70%),
              linear-gradient(160deg, #1E1A24 0%, #100E16 100%);
            box-shadow: 0 0 40px rgba(252, 155, 14, 0.12);
          }
          .lop-code {
            font-family: "Prompt", ui-monospace, monospace;
            text-shadow: 0 0 18px rgba(252, 155, 14, 0.45);
          }
          /* Halo pulsé du bouton CONTACT du footer, redéclaré ici pour que la
             page ne dépende pas du rendu du footer. */
          @keyframes glowPulse {
            0%, 100% { box-shadow: 0 0 0px #FFD700; }
            50%      { box-shadow: 0 0 12px #FFD700; }
          }
          .lop-reveal { animation: lopFade 220ms ease-out both; }
          @keyframes lopFade {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .lop-shake { animation: lopShake 250ms ease-in-out; }
          @keyframes lopShake {
            0%, 100% { transform: translateX(0); }
            25%      { transform: translateX(-6px); }
            75%      { transform: translateX(6px); }
          }
          @media (prefers-reduced-motion: reduce) {
            .lop-reveal, .lop-shake { animation: none; }
          }
        `}</style>

        {/* Bandeau haut : même icône et même liseré doré que le reste du site. */}
        <header className="w-full bg-black">
          <div className="mx-auto flex h-16 max-w-2xl items-center justify-between px-4">
            <a href="/" aria-label={t.brand.logoAlt}>
              <motion.img
                whileHover={{ scale: 1.1 }}
                src="/assets/Icon_legends_of_the_past.svg"
                title={t.brand.logoAlt}
                alt={t.brand.logoAlt}
                className="w-12"
              />
            </a>
            <LanguageSwitch
              locale={locale}
              onChange={setLocale}
              label={t.languageLabel}
            />
          </div>
        </header>
        <div className="h-[6px] w-full bg-gradient-to-r from-[#AC6A33] via-[#E3DB5C] to-[#E5AE20]" />

        <main className="mx-auto w-full max-w-2xl px-4 pb-12 pt-5 sm:px-6">
          <div className="mb-5 flex flex-col items-center text-center">
            <h1>
              <img
                src={LOGO_URL}
                alt={t.brand.logoAlt}
                className="mx-auto w-full max-w-[9rem] sm:max-w-[12rem]"
              />
            </h1>
            <p className={TITLE_CLASS + " mt-2 text-xl sm:text-3xl"}>
              {t.brand.subtitle}
            </p>
          </div>

          <Explainer />

          {/* ---------- Écran d'accueil : mot de passe ---------- */}
          {step === "gate" && (
            <div className="lop-reveal flex flex-col gap-4">
              <div className={shake ? "lop-shake" : undefined}>
                <label
                  htmlFor="lop-password"
                  className={"mb-4 block text-center text-white/85 " + BODY_CLASS}
                >
                  {t.gate.label}
                </label>
                <input
                  id="lop-password"
                  name="password"
                  type="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck="false"
                  placeholder={t.gate.placeholder}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value.toUpperCase());
                    setGateError(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") checkPassword();
                  }}
                  aria-invalid={gateError ? "true" : "false"}
                  aria-describedby={gateError ? "lop-password-error" : undefined}
                  className="w-full rounded-2xl border-2 border-white/20 bg-[color:var(--lop-surface)] px-4 py-5 text-center text-2xl font-bold uppercase tracking-[0.25em] text-white placeholder-white/25 transition-colors duration-200 hover:border-white/40 focus:border-[#FFD700] sm:py-6 sm:text-3xl"
                />
                {gateError && (
                  <p
                    id="lop-password-error"
                    role="alert"
                    className="mt-3 text-center text-sm text-[color:var(--lop-error)]"
                  >
                    {t.gate[gateError]}
                  </p>
                )}
              </div>

              <PrimaryButton
                loading={gateLoading}
                loadingLabel={t.gate.loading}
                onClick={checkPassword}
              >
                {t.gate.submit}
              </PrimaryButton>

              <button
                type="button"
                onClick={goToApply}
                className="w-full rounded-xl border border-white/30 px-6 py-3 text-sm font-bold uppercase tracking-wide text-white/80 transition-colors duration-200 hover:border-white hover:text-white"
              >
                {t.gate.secondary}
              </button>
            </div>
          )}

          {/* ---------- Parcours A : génération immédiate ----------
              Pas de titre de section ici : le bouton porte déjà l'action. */}
          {step === "generate" && (
            <div className="lop-reveal flex flex-col gap-6">
              <PseudoField
                id="lop-generate-pseudo"
                value={pseudo}
                onChange={setPseudo}
                error={pseudoError}
              />

              <SocialsBlock
                idPrefix="lop-generate"
                socials={socials}
                onChange={updateSocial}
              />

              <Honeypot
                id="lop-generate-company"
                value={honeypot}
                onChange={setHoneypot}
              />

              {networkError && (
                <NetworkError
                  retrying={submitting}
                  onRetry={() => send("generate", pseudo)}
                />
              )}
              {rejection && <RejectionNotice message={t.errors[rejection]} />}
              {conflictVariants && (
                <ConflictNotice
                  variants={conflictVariants}
                  onPick={(variant) => {
                    setPseudo(variant);
                    send("generate", variant);
                  }}
                />
              )}

              {!result && (
                <PrimaryButton
                  disabled={!generateReady}
                  loading={submitting}
                  loadingLabel={t.generate.loading}
                  onClick={() => send("generate", pseudo)}
                >
                  {t.generate.submit}
                </PrimaryButton>
              )}

              <div ref={resultRef}>
                {result && (
                  <CodeCard
                    code={result.code}
                    pending={result.pending}
                  />
                )}
              </div>
            </div>
          )}

          {/* ---------- Parcours B : candidature ---------- */}
          {step === "apply" && (
            <div className="lop-reveal flex flex-col gap-6">
              <h2 className={TITLE_CLASS + " text-2xl sm:text-4xl"}>
                {t.apply.title}
              </h2>

              <div>
                <FieldLabel htmlFor="lop-motivation">
                  {t.apply.motivationLabel}
                </FieldLabel>
                <textarea
                  id="lop-motivation"
                  name="motivation"
                  rows={5}
                  placeholder={t.apply.motivationPlaceholder}
                  value={motivation}
                  onChange={(event) => setMotivation(event.target.value)}
                  aria-describedby="lop-motivation-counter"
                  className={inputClass + " resize-y leading-relaxed"}
                />
                <p
                  id="lop-motivation-counter"
                  className={
                    "mt-2 " + BODY_CLASS + " " +
                    (motivationValid
                      ? "text-[color:var(--lop-success)]"
                      : "text-white/60")
                  }
                >
                  {motivationValid
                    ? t.apply.counterDone(motivation.trim().length)
                    : t.apply.counter(motivation.trim().length)}
                </p>
              </div>

              {/* Le reste apparaît dès 30 caractères et ne se referme plus. */}
              {applyRevealed && (
                <div className="lop-reveal flex flex-col gap-6">
                  <PseudoField
                    id="lop-apply-pseudo"
                    value={pseudo}
                    onChange={setPseudo}
                    error={pseudoError}
                  />

                  <SocialsBlock
                    idPrefix="lop-apply"
                    socials={socials}
                    onChange={updateSocial}
                  />

                  <div>
                    <FieldLabel htmlFor="lop-email">
                      {t.apply.emailLabel}
                    </FieldLabel>
                    <input
                      id="lop-email"
                      name="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder={t.apply.emailPlaceholder}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      aria-invalid={email !== "" && !emailValid ? "true" : "false"}
                      className={inputClass}
                    />
                    {email !== "" && !emailValid && (
                      <p className="mt-1 text-sm text-[color:var(--lop-error)]">
                        {t.apply.emailInvalid}
                      </p>
                    )}
                  </div>

                  <Honeypot
                    id="lop-apply-company"
                    value={honeypot}
                    onChange={setHoneypot}
                  />
                </div>
              )}

              {networkError && (
                <NetworkError
                  retrying={submitting}
                  onRetry={() => send("application", pseudo)}
                />
              )}
              {rejection && <RejectionNotice message={t.errors[rejection]} />}
              {conflictVariants && (
                <ConflictNotice
                  variants={conflictVariants}
                  onPick={(variant) => {
                    setPseudo(variant);
                    send("application", variant);
                  }}
                />
              )}

              {!result && (
                <PrimaryButton
                  disabled={!applyReady}
                  loading={submitting}
                  loadingLabel={t.apply.loading}
                  onClick={() => send("application", pseudo)}
                >
                  {t.apply.submit}
                </PrimaryButton>
              )}

              <div ref={resultRef}>
                {result && (
                  <CodeCard
                    code={result.code}
                    pending={result.pending}
                  />
                )}
              </div>

              {!result && (
                <button
                  type="button"
                  onClick={() => {
                    resetForms();
                    setStep("gate");
                  }}
                  className="mx-auto text-sm text-white/60 underline transition-colors duration-200 hover:text-white"
                >
                  {t.apply.back}
                </button>
              )}
            </div>
          )}
        </main>

        <Footer />
      </section>
    </LocaleContext.Provider>
  );
};

export default Creator;
